import type { DailyResetService } from "@yjsgames/daily-reset";
import type { TutorialRunner } from "@yjsgames/phaser-tutorial";
import { buildAutoPlaySnapshot, type AutoPlaySnapshot } from "../app/buildAutoPlaySnapshot.js";
import type { GameContext } from "../app/GameContext.js";
import type { ProgressState } from "../app/saveSwap.js";
import type { ShellSystems } from "../app/createShellSystems.js";
import type { TabRouter } from "../app/TabRouter.js";
import type { DomainControllers } from "../app/createControllers.js";
import type { ModalHost } from "../ui/ModalHost.js";
import type { DailyShopTabData } from "../ui/DailyShopTabView.js";
import type { BattlefieldRunner } from "./BattlefieldRunner.js";
import type { DungeonFlow } from "./DungeonFlow.js";
import type { GuideQuestPanelDebugSnapshot } from "../ui/GuideQuestPanel.js";

export interface ResetGameSceneSaveDeps {
  readonly shell: ShellSystems;
  readonly controllers: DomainControllers;
  readonly registryGet: (key: string) => unknown;
  readonly restart: () => void;
}

export function resetGameSceneSave(deps: ResetGameSceneSaveDeps): void {
  deps.shell.persistScheduler.cancel();
  void deps.controllers.saveSystem.clear().then(
    () => {
      (deps.registryGet("onDeleteCloudAccount") as (() => void) | undefined)?.();
      deps.restart();
    },
    (e: unknown) => console.error("[GameScene][resetSave] saveSystem.clear failed", e),
  );
}

export function startGameSceneTutorial(runner: TutorialRunner, scenarioId = "main"): void {
  runner.startScenario(scenarioId, { forceReplay: true });
}

export interface ResetGameSceneTutorialDeps {
  readonly progress: ProgressState;
  readonly persist: () => void;
  readonly runner: TutorialRunner;
}

export function resetGameSceneTutorial(
  deps: ResetGameSceneTutorialDeps,
  scenarioId = "main",
): void {
  deps.progress.tutorialResume[scenarioId] = 0;
  const i = deps.progress.tutorialCompleted.indexOf(scenarioId);
  if (i >= 0) deps.progress.tutorialCompleted.splice(i, 1);
  deps.persist();
  deps.runner.startScenario(scenarioId, { forceReplay: true });
}

export interface GameSceneAutoPlaySnapshotDeps {
  readonly ctx: GameContext;
  readonly shell: ShellSystems;
  readonly dailyReset: () => DailyResetService;
  readonly tabRouter: TabRouter;
  readonly modals: ModalHost;
  readonly runner: BattlefieldRunner;
  readonly dungeonFlow: DungeonFlow;
  readonly dailyShopData: () => DailyShopTabData;
  readonly tutorialRunner: TutorialRunner;
  readonly guideQuestPanel: () => GuideQuestPanelDebugSnapshot;
}

export function getGameSceneAutoPlaySnapshot(
  deps: GameSceneAutoPlaySnapshotDeps,
): AutoPlaySnapshot {
  return buildAutoPlaySnapshot({
    ctx: deps.ctx,
    combat: deps.shell.combat,
    dailyReset: deps.dailyReset,
    activeTab: () => deps.tabRouter.current(),
    settingsOpen: () => deps.modals.settingsOpen,
    activeBricks: () => deps.runner.countActiveBricks(),
    dungeonMode: () => deps.dungeonFlow.isActive(),
    dailyShopData: deps.dailyShopData,
    tutorialActive: () => deps.tutorialRunner.isActive,
    tutorialStepId: () => deps.tutorialRunner.currentStepId,
    guideQuestPanel: deps.guideQuestPanel,
  });
}
